/**
 * Tożsamość wołającego — migawka TEJ karty.
 *
 * Backend (`/u/fc4/web/web_authz.i`) buduje z `sv_numrecuser` zakres widocznych
 * kontrahentów (can_see_partner / user_sees_all / ttAuthzAllow), więc numer
 * użytkownika decyduje o tym, CZYJE dane zobaczy pytający.
 *
 * Klucz `firmatic-auth` w localStorage jest wspólny dla całego originu, a jeden
 * origin (zks.firmatic.pl:9443) obsługuje DWIE instancje Firmatic — preprod
 * (2.99) i produkcję (2.100). Odczyt per żądanie sprawiał, że karta produkcji
 * doklejała `sv_numrecuser` zaseedowany przez kartę preprodu; ten sam NumRec to
 * w drugiej bazie INNA osoba, więc użytkownik cicho dostawał cudzy zakres
 * widoczności. To błąd poprawnościowy w trybie wypadku (dwie karty), nie nowa
 * luka — seed jest client-side i zawsze był podrabialny, dlatego brama dostała
 * walidację tokenem (`loginWithSsoToken`).
 *
 * Dlatego localStorage czytamy RAZ (seed pulpitu, zanim store zdąży się
 * odezwać), a każda późniejsza zmiana stanu logowania wpycha tożsamość PUSHEM
 * ze store'a (auth.saveToStorage / auth.logout) — nigdy odwrotnie. Ten sam
 * wzorzec, co migawka konfiguracji BFF w config.service.
 *
 * Moduł celowo nie importuje niczego: api.client jest ładowany przez router
 * i store'y, więc sięgnięcie tu po `useAuthStore()` zrobiłoby cykl przy starcie.
 */
export interface CallerIdentity {
  /** Users.NumRec pracownika (`sv_numrecuser`) */
  employeeId: string | null;
  /** Partners.NumRec kontrahenta zalogowanego na siebie (`sv_numrecpartner`) */
  partnerId: string | null;
}

const AUTH_STORAGE_KEY = 'firmatic-auth';
const EMPTY: CallerIdentity = { employeeId: null, partnerId: null };

/** Pusty string i whitespace znaczą „brak" — koszyk kontrahenta wysyłał `''`. */
function normalize(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

class IdentityService {
  /** null = jeszcze nie zaseedowana; obiekt = migawka tej karty (także pusta po wylogowaniu) */
  private snapshot: CallerIdentity | null = null;

  /** Tożsamość do doklejenia w żądaniu. Pierwszy odczyt seeduje z localStorage. */
  get(): CallerIdentity {
    if (!this.snapshot) {
      this.snapshot = this.readSeed();
    }
    return { ...this.snapshot };
  }

  /** Stan logowania tej karty (auth store) nadpisuje migawkę. */
  set(identity: Partial<CallerIdentity>): void {
    this.snapshot = {
      employeeId: normalize(identity.employeeId),
      partnerId: normalize(identity.partnerId),
    };
  }

  /**
   * Wylogowanie: migawka zostaje PUSTA, a nie „nieustawiona".
   * Ponowny odczyt seeda wciągnąłby tu tożsamość z cudzej karty — czyli dokładnie
   * to, przed czym ta klasa broni.
   */
  clear(): void {
    this.snapshot = { ...EMPTY };
  }

  private readSeed(): CallerIdentity {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return { ...EMPTY };
      const state = JSON.parse(raw);
      return {
        employeeId: normalize(state?.employeeId),
        partnerId: normalize(state?.partnerId),
      };
    } catch {
      // uszkodzony wpis nie może zablokować wywołania — backend i tak odrzuci
      // żądanie bez tożsamości, gdy authz.strict będzie włączone
      return { ...EMPTY };
    }
  }
}

export const identityService = new IdentityService();
